import { useState, useEffect } from 'react';
import OpenAI from 'openai';
import { responseCache } from '../services/cache';
import { RetryService } from '../services/retry';
import { PersonalityService } from '../services/personality';
import { microphoneService } from '../services/microphone';
import { VisionService } from '../services/ai/vision';
import { SentimentService } from '../services/ai/sentiment';
import { VisionCommandService } from '../services/ai/vision-commands';
import { useReminders } from './useReminders';
import { alexaSmartHome } from '../services/smart-home/alexa';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

const defaultTone: ToneStyle = 'normal';

export const useAI = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [microphoneStatus, setMicrophoneStatus] = useState<{
    isWorking: boolean;
    message: string;
  } | null>(null);
  const [tone, setTone] = useState<ToneStyle>(defaultTone);

  const personalityService = new PersonalityService();
  const { addVoiceReminder } = useReminders();

  useEffect(() => {
    // Verifica o microfone ao iniciar
    checkMicrophone();

    // Limpa o monitoramento ao desmontar
    return () => {
      microphoneService.stopMonitoring();
    };
  }, []);

  const checkMicrophone = async () => {
    const status = await microphoneService.checkMicrophone();
    setMicrophoneStatus(status);
    
    if (status.isWorking) {
      microphoneService.startMonitoring();
    }
  };

  const optimizePrompt = (message: string): string => {
    return `Você é J.A.R.V.I.S., assistente pessoal do Luiz. Mantenha respostas diretas e eficientes, com um toque de sagacidade quando apropriado. Foque em soluções práticas e inovadoras. Mensagem: ${message}`;
  };

  const processMessage = async (message: string): Promise<string> => {
    try {
      setIsProcessing(true);

      const msg = message.toLowerCase();
      
      // Verifica se é comando para dispositivos
      if (msg.includes('ligar') || msg.includes('desligar') || 
          msg.includes('ajustar') || msg.includes('aumentar') || 
          msg.includes('diminuir')) {
        return await alexaSmartHome.controlDevice(message);
      }

      // Verifica se é um comando do Vision
      const visionResponse = await VisionCommandService.processCommand(message);
      if (visionResponse) {
        return visionResponse;
      }

      // Verifica se é um lembrete
      if (msg.includes('lembrar') || msg.includes('lembrete') || msg.includes('agendar')) {
        const reminder = addVoiceReminder(message);
        return `Lembrete agendado para ${reminder.datetime.toLocaleString()}`;
      }

      // Verifica se é comando de imagem
      if (message.toLowerCase().includes('gerar imagem')) {
        const imageUrl = await VisionService.generateImage(message);
        return `Imagem gerada: ${imageUrl}`;
      }

      // Verifica se tem URL de imagem
      const imageUrlMatch = message.match(/https?:\/\/[^\s]+\.(jpg|jpeg|png|gif|webp)/i);
      if (imageUrlMatch) {
        const analysis = await VisionService.analyzeImage(imageUrlMatch[0]);
        return analysis;
      }

      // Processa mensagem normal
      const response = await processMessageWithGPT(message);
      
      // Tenta analisar sentimento, mas não falha se der erro
      try {
        const sentiment = await SentimentService.analyze(message);
        return personalityService.enhanceResponse(response, sentiment.sentiment, tone);
      } catch {
        return personalityService.enhanceResponse(response, 'normal', tone);
      }

    } catch (error) {
      console.error('Erro ao processar mensagem:', error);
      return personalityService.getErrorMessage(tone);
    } finally {
      setIsProcessing(false);
    }
  };

  const processMessageWithGPT = async (message: string): Promise<string> => {
    // Verifica cache
    const cachedResponse = responseCache.get(message);
    if (cachedResponse) {
      return cachedResponse;
    }

    // Verifica o status do microfone
    if (!await microphoneService.checkMicrophone()) {
      return personalityService.getErrorMessage(tone);
    }

    // Faz chamada API
    const response = await RetryService.withRetry(
      async () => {
        const completion = await openai.chat.completions.create({
          messages: [
            {
              role: "system",
              content: "Você é J.A.R.V.I.S., o assistente pessoal sofisticado de Luiz. Mantenha suas respostas profissionais e eficientes, com toques de sagacidade. Faça referências ocasionais aos interesses dele (IA, e-commerce, produtividade) e suas inspirações (Tony Stark, Batman, etc). Priorize soluções práticas e mantenha o foco em seus objetivos de excelência e inovação."
            },
            {
              role: "user",
              content: optimizePrompt(message)
            }
          ],
          model: "gpt-3.5-turbo",
          temperature: 0.7,
          max_tokens: 150,
          presence_penalty: 0.6,
          frequency_penalty: 0.2
        });

        return completion.choices[0]?.message?.content || 
          personalityService.getErrorMessage(tone);
      },
      {
        maxAttempts: 3,
        delayMs: 1000,
        backoffFactor: 1.5
      }
    );
    
    responseCache.set(message, response);
    return response;
  };

  return {
    processMessage,
    tone,
    setTone,
    isProcessing,
    microphoneStatus,
    checkMicrophone,
    getWaitingMessage: personalityService.getWaitingMessage.bind(personalityService)
  };
};

export default useAI;